import {
  getPurse,
  getTournamentType,
  isRoundNumber456,
} from "@/utils/tournaments";
import {
  AGGREGATE_CTP_FINALE,
  AGGREGATE_CTP_LAST,
  BIRDIE_STREAK_LAST,
  INITIAL_PURSE,
  LONGEST_DRIVE_LAST,
  MOST_GIRS_LAST,
  SOCIAL_MEDIA_FINALE,
  SOCIAL_MEDIA_LAST,
  TOURNAMENT_IDS_SEASON_1,
} from "@/utils/constants";
import { PRIZE_TABLE } from "@/components/PrizeList/PrizeListSeason1";
import CalculatePrize from "./CalculatePrize";
import PurseCard from "./PurseCard";
import FullPrizeList from "./FullPrizeList";

interface PurseSeason1Props {
  playersCount: number;
  tournamentName: string;
  inputVal: string;
  tournamentId: string;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const PurseSeason1 = ({
  playersCount,
  tournamentName,
  inputVal,
  tournamentId,
  handleInputChange,
}: PurseSeason1Props) => {
  const type = getTournamentType(1, tournamentId, tournamentName);

  const isRound456 = isRoundNumber456(tournamentName);
  const tournamentRoundSeason1 = `round_${
    TOURNAMENT_IDS_SEASON_1.indexOf(tournamentId) + 1
  }`;

  const prizeList = PRIZE_TABLE[tournamentRoundSeason1];

  if (type.isFinalSeason1) {
    return (
      <div className="flex justify-center">
        <div className="w-full min-[1041px]:!max-w-[965px]">
          <ul className="flex flex-col md:flex-row gap-5 md:gap-36 lg:gap-40 xl:gap-48 p-0 w-full list-none mb-[50px]">
            <li className="flex flex-col gap-2.5">
              <span className="text-4xl 2xl:text-6xl">
                ${INITIAL_PURSE.toLocaleString("en-US")}
              </span>
              <span className="text-xl text-tm-c-gray-300">Purse</span>
            </li>
            <li className="flex flex-col gap-2.5">
              <span className="text-4xl 2xl:text-6xl">{playersCount}</span>
              <span className="text-xl text-tm-c-gray-300">Players</span>
            </li>
          </ul>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 2xl:gap-[30px]">
            <CalculatePrize
              prizeList={prizeList}
              inputVal={inputVal}
              handleInputChange={handleInputChange}
            />
            <PurseCard
              title="Stroke play"
              prizeList={Array.from({ length: 5 }, (_, i) => prizeList[i] ?? 0)}
            />
            <PurseCard title="Aggregate CTP" prizeList={AGGREGATE_CTP_FINALE} />
            <PurseCard title="Social Media" prizeList={SOCIAL_MEDIA_FINALE} />
          </div>
          <FullPrizeList />
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <div className="w-full min-[1041px]:!max-w-[965px]">
        <ul className="flex flex-col md:flex-row gap-5 md:gap-36 lg:gap-40 xl:gap-48 p-0 w-full list-none mb-[50px]">
          <li className="flex flex-col gap-2.5">
            <span className="text-4xl 2xl:text-6xl">
              $
              {getPurse(
                1,
                tournamentId,
                tournamentName,
                playersCount
              ).toLocaleString("en-US")}
            </span>
            <span className="text-xl text-tm-c-gray-300">Purse</span>
          </li>
          <li className="flex flex-col gap-2.5">
            <span className="text-4xl 2xl:text-6xl">{playersCount}</span>
            <span className="text-xl text-tm-c-gray-300">Players</span>
          </li>
        </ul>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5 2xl:gap-[30px]">
          <CalculatePrize
            prizeList={prizeList}
            inputVal={inputVal}
            handleInputChange={handleInputChange}
          />
          <PurseCard
            title="Stroke play"
            prizeList={Array.from({ length: 5 }, (_, i) => prizeList[i] ?? 0)}
          />
          <PurseCard title="Aggregate CTP" prizeList={AGGREGATE_CTP_LAST} />
          <PurseCard title="Birdie Streak" prizeList={BIRDIE_STREAK_LAST} />
          <PurseCard
            title="Longest Drive - Men"
            prizeList={LONGEST_DRIVE_LAST}
          />
          <PurseCard
            title="Longest Drive - Women"
            prizeList={LONGEST_DRIVE_LAST}
          />
          {!isRound456 && (
            <PurseCard title="Most GIRs" prizeList={MOST_GIRS_LAST} />
          )}
          <div
            className={`flex flex-col gap-2.5 lg:gap-7.5 min-h-[165px] p-5 lg:p-7.5
                rounded-[10px] bg-tm-c-gray-950 border border-tm-c-gray-700
                lg:min-h-[200px] lg:py-6 lg:px-[30px]
                min-[1041px]:!min-h-[300px]
              `}
          >
            <span className="text-xs md:text-base text-left">Social Media</span>
            <ul className="text-base lg:text-xl my-2.5 mx-0 w-full list-none p-0 [&>li]:flex [&>li]:w-full [&>li]:justify-between">
              <li>
                <span>
                  1<span className="align-super text-xs">st</span>
                </span>
                <span className="font-medium">${SOCIAL_MEDIA_LAST}</span>
              </li>
              <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
              <li>
                <span>
                  2<span className="align-super text-xs">nd</span>
                </span>
                <span className="font-medium">${SOCIAL_MEDIA_LAST}</span>
              </li>
              <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
              <li>
                <span>
                  3<span className="align-super text-xs">rd</span>
                </span>
                <span className="font-medium">${SOCIAL_MEDIA_LAST}</span>
              </li>
              <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
              <li>
                <span>
                  4<span className="align-super text-xs">th</span>
                </span>
                <span className="font-medium">${SOCIAL_MEDIA_LAST}</span>
              </li>
              <span className="flex w-full h-[1px] bg-tm-c-gray-800 my-2" />
              <li>
                <span>
                  5<span className="align-super text-xs">th</span>
                </span>
                <span className="font-medium">${SOCIAL_MEDIA_LAST}</span>
              </li>
            </ul>
          </div>
        </div>
        <FullPrizeList />
      </div>
    </div>
  );
};

export default PurseSeason1;
